import React, { useState } from "react";
import Visibility from "@mui/icons-material/VisibilityOutlined";
import VisibilityOff from "@mui/icons-material/VisibilityOffOutlined";
import { useNavigate } from "react-router-dom";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { _admin_login_api } from "../../../DAL/Login/Auth";
import { _set_token_in_localStorage } from "../../../local_storage/local_storage";
import { useSnackbar } from "notistack";
import { formatString, validateInputs } from "../../../utils/constant";
import { useSidebarStatus } from "../../../Hooks/App_Context";
import { useAdminContext } from "../../../Hooks/AdminContext";

const RequiredInputFields = ["email", "password"];

const LoginForm = ({ setFormState }) => {
  const navigate = useNavigate();
  const { START_SOCKET_FORCEFULLY } = useAdminContext();
  const { updateData } = useSidebarStatus();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [inputs, setInputs] = useState({
    email: "",
    password: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // This function handles the login form submission. It performs the following steps:
  // 1. Prevents the default form submission behavior.
  // 2. Validates the password length to ensure it's at least 8 characters. If not, it displays an error message.
  // 3. Saves the email to localStorage for persistence across sessions.
  // 4. Sets the loading state to true, indicating that login is in progress.
  // 5. Prepares the `postData` with email and password and performs validation using `validateInputs`.
  // 6. If there are any missing required fields, it displays an error notification.
  // 7. If no errors, it proceeds to send the login request via the `_admin_login_api` function.
  // 8. If the login is successful (response code 200), it starts the socket connection, updates employee data in the global state,
  //    stores the user ID and token in localStorage, and navigates the user to the dashboard.
  // 9. If the login fails, it shows the error message and stops the loading state.
  const handleSubmitLogin = async (e) => {
    e.preventDefault();

    if (inputs.password.length < 8) {
      enqueueSnackbar("Password should be at least 8 characters long", {
        variant: "error",
      });
      return;
    }
    localStorage.setItem("email", inputs.email);
    setIsLoading(true);
    let postData = {
      type: 0,
      email: inputs.email,
      password: inputs.password,
    };
    const errors = validateInputs(inputs, RequiredInputFields, [], []);
    if (!(Object.keys(errors).length === 0)) {
      enqueueSnackbar(formatString(Object.keys(errors)[0]) + " is required", {
        variant: "error",
      });
      return;
    }

    const result = await _admin_login_api(postData);
    if (result.code === 200) {
      START_SOCKET_FORCEFULLY(result?.user?.user_id?._id);
      updateData({
        employee_data: result?.user,
      });
      localStorage.setItem("UserId", result.user.user_id._id);
      _set_token_in_localStorage(result.token);
      navigate(`/dashboard`, { replace: true });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="col-12 text-center ">
        <h3 className="login-text">Login</h3>
      </div>
      <form onSubmit={handleSubmitLogin}>
        <div className="col-12 mt-4">
          <TextField
            id="outlined-basic"
            label="E-mail"
            name="email"
            variant="outlined"
            fullWidth
            type="email"
            required
            placeholder="E-mail"
            value={inputs.email}
            onChange={handleChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </div>
        <div className="col-12 mt-3 mb-1">
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="outlined-adornment-password">
              Password *
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              type={showPassword ? "text" : "password"}
              placeholder="Password *"
              fullWidth
              required
              InputLabelProps={{
                shrink: true,
              }}
              name="password"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <Visibility
                        style={{
                          fontSize: "18px",
                        }}
                      />
                    ) : (
                      <VisibilityOff
                        style={{
                          fontSize: "18px",
                        }}
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
              value={inputs.password}
              onChange={handleChange}
            />
          </FormControl>
        </div>

        <div className="col-12 mt-2">
          <div className="position-relative">
            <div
              className="float-end pb-3 pointer"
              onClick={() => {
                localStorage.setItem("state", 0);
                setFormState(0);
              }}
            >
              {/* eslint-disable-next-line */}
              <a href="#" className="text-muted">
                Forgot Password
              </a>
            </div>
          </div>
        </div>

        <div className="mt-3">
          <Button
            type="submit"
            variant="contained"
            className="w-100 login-page-button"
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Login"}
          </Button>
        </div>
      </form>
    </>
  );
};

export default LoginForm;
